import { ConfigService } from '@teachino/shared-util';
import { helpUrl } from './constants';

type ProductlaneConfig = {
  productlaneEnabled: () => boolean;
};

type ProductlaneInitOptions = {
  widgetKey: string;
  userToken?: string;
  position?: 'left' | 'right' | 'center';
};

type ProductlaneSetOptions = {
  userToken?: string;
  position?: 'left' | 'right' | 'center';
};

type ProductlaneEventHandler = () => void;

export type ProductlaneView =
  | 'INDEX'
  | 'CHANGELOG'
  | 'DOCS'
  | 'FEEDBACK'
  | 'AICHAT'
  | 'LIVE_CHAT';

type ProductlaneScriptState = 'idle' | 'loading' | 'loaded' | 'failed';

type ProductlaneQueuedMethod =
  | 'init'
  | 'enable'
  | 'disable'
  | 'set'
  | 'open'
  | 'close'
  | 'toggle'
  | 'on'
  | 'off'
  | 'signOut'
  | 'setContext'
  | 'clearContext';

type ProductlaneQueueEntry = {
  args: IArguments | unknown[];
};

type ProductlaneGlobal = {
  queue: Partial<Record<ProductlaneQueuedMethod, ProductlaneQueueEntry>>;
  init: (options: ProductlaneInitOptions) => void;
  enable: () => void;
  disable: () => void;
  set: (options: ProductlaneSetOptions) => void;
  open: (view?: ProductlaneView) => void;
  close: () => void;
  toggle: () => void;
  on: (event: string, handler: ProductlaneEventHandler) => void;
  off: (event: string, handler: ProductlaneEventHandler) => void;
  signOut: () => void;
  setContext: (context: Record<string, string>) => void;
  clearContext: () => void;
};

type ProductlaneCredentials = {
  widgetKey: string;
  userToken: string;
};

type ProductlaneWindow = Window & {
  productlaneInitialised?: boolean;
  productlaneScriptState?: ProductlaneScriptState;
  productlaneCredentials?: ProductlaneCredentials;
  productlaneSkipLogged?: boolean;
  productlaneWidgetReady?: boolean;
};

const PRODUCTLANE_GLOBAL_NAME = 'Productlane';
const PRODUCTLANE_SCRIPT_ID = 'productlane-widget-script';
const PRODUCTLANE_SCRIPT_SRC =
  'https://widget.productlane.com/latest.productlane-widget.min.js';
const PRODUCTLANE_LOG_PREFIX = '[Productlane]';
const PRODUCTLANE_LOADED_EVENT = 'loaded';
const PRODUCTLANE_READY_TIMEOUT_IN_MS = 10000;
const PRODUCTLANE_READY_POLL_INTERVAL_IN_MS = 100;
const config: ProductlaneConfig = new ConfigService();

export const isProductlaneEnabled = (): boolean => {
  return config.productlaneEnabled();
};

const canUseBrowserApis = (): boolean =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const getProductlaneWindow = (): ProductlaneWindow => window as ProductlaneWindow;

// This wrapper is intentionally scoped to the feedback-widget lifecycle.
// It is not a full generic Productlane command bus.

const getProductlaneApi = (): ProductlaneGlobal | undefined => {
  const productlaneWindow = getProductlaneWindow() as ProductlaneWindow &
    Record<string, ProductlaneGlobal | undefined>;
  return productlaneWindow[PRODUCTLANE_GLOBAL_NAME];
};

const setProductlaneApi = (productlane: ProductlaneGlobal): void => {
  const productlaneWindow = getProductlaneWindow() as ProductlaneWindow &
    Record<string, ProductlaneGlobal | undefined>;
  productlaneWindow[PRODUCTLANE_GLOBAL_NAME] = productlane;
};

const ensureProductlaneApi = (): ProductlaneGlobal => {
  const existingProductlane = getProductlaneApi();
  if (existingProductlane) {
    return existingProductlane;
  }

  const productlane = {
    queue: {},
  } as ProductlaneGlobal;

  const queueMethod = <TMethod extends ProductlaneQueuedMethod>(
    method: TMethod,
  ): ProductlaneGlobal[TMethod] => {
    return ((...args: Parameters<ProductlaneGlobal[TMethod]>) => {
      productlane.queue[method] = { args };
    }) as ProductlaneGlobal[TMethod];
  };

  productlane.init = queueMethod('init');
  productlane.enable = queueMethod('enable');
  productlane.disable = queueMethod('disable');
  productlane.set = queueMethod('set');
  productlane.open = queueMethod('open');
  productlane.close = queueMethod('close');
  productlane.toggle = queueMethod('toggle');
  productlane.on = queueMethod('on');
  productlane.off = queueMethod('off');
  productlane.signOut = queueMethod('signOut');
  productlane.setContext = queueMethod('setContext');
  productlane.clearContext = queueMethod('clearContext');

  setProductlaneApi(productlane);
  return productlane;
};

const loadProductlaneScript = (): void => {
  if (!canUseBrowserApis()) {
    return;
  }

  const productlaneWindow = getProductlaneWindow();
  if (productlaneWindow.productlaneScriptState === 'loaded' || productlaneWindow.productlaneScriptState === 'loading') {
    return;
  }

  const existingScript = document.getElementById(PRODUCTLANE_SCRIPT_ID);
  if (existingScript) {
    if (productlaneWindow.productlaneScriptState === 'failed') {
      existingScript.remove();
    } else {
      productlaneWindow.productlaneScriptState = 'loading';
      return;
    }
  }

  const script = document.createElement('script');
  script.id = PRODUCTLANE_SCRIPT_ID;
  script.async = true;
  script.defer = true;
  script.crossOrigin = 'anonymous';
  script.src = PRODUCTLANE_SCRIPT_SRC;
  productlaneWindow.productlaneScriptState = 'loading';
  script.addEventListener('load', () => {
    productlaneWindow.productlaneScriptState = 'loaded';
  });
  script.addEventListener('error', (error) => {
    productlaneWindow.productlaneInitialised = false;
    productlaneWindow.productlaneScriptState = 'failed';
    console.error(`${PRODUCTLANE_LOG_PREFIX} Failed to load Productlane widget script.`, error);
  });
  document.head.appendChild(script);
};

// The loader swaps the queueing stub for the real API once it has bootstrapped.
// Only the real API forwards commands to the widget; the stub would silently
// queue them, because the loader replays `init` and `on` but nothing else.
const isBootstrappedProductlaneApi = (
  productlane: ProductlaneGlobal | undefined,
): productlane is ProductlaneGlobal =>
  productlane !== undefined && !('queue' in productlane);

const hasProductlaneWidgetSettled = (): boolean => {
  const productlaneWindow = getProductlaneWindow();
  return (
    productlaneWindow.productlaneWidgetReady === true ||
    productlaneWindow.productlaneScriptState === 'failed'
  );
};

// The widget drops commands that arrive before its iframe has attached its own
// message listener, so a view can only be opened after the widget reported that
// it is loaded. Resolves after the timeout as well, so a missing event degrades
// to the previous best-effort behaviour instead of blocking the help menu.
const waitForProductlaneWidget = (): Promise<void> =>
  new Promise((resolve) => {
    if (hasProductlaneWidgetSettled()) {
      resolve();
      return;
    }

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      const hasTimedOut =
        Date.now() - startedAt >= PRODUCTLANE_READY_TIMEOUT_IN_MS;

      if (hasProductlaneWidgetSettled() || hasTimedOut) {
        window.clearInterval(intervalId);
        resolve();
      }
    }, PRODUCTLANE_READY_POLL_INTERVAL_IN_MS);
  });

const resetProductlaneState = (): void => {
  if (!canUseBrowserApis()) {
    return;
  }

  const productlaneWindow = getProductlaneWindow() as ProductlaneWindow &
    Record<string, ProductlaneGlobal | undefined>;
  productlaneWindow.productlaneInitialised = false;
  productlaneWindow.productlaneScriptState = 'idle';
  productlaneWindow.productlaneCredentials = undefined;
  productlaneWindow.productlaneSkipLogged = false;
  productlaneWindow.productlaneWidgetReady = false;
  productlaneWindow[PRODUCTLANE_GLOBAL_NAME] = undefined;
  document.getElementById(PRODUCTLANE_SCRIPT_ID)?.remove();
};

export const initialiseProductlaneWidget = (
  widgetKey: string,
  userToken: string,
): void => {
  if (!canUseBrowserApis()) {
    return;
  }

  if (!isProductlaneEnabled()) {
    resetProductlaneState();
    return;
  }

  const productlaneWindow = getProductlaneWindow();
  const productlane = ensureProductlaneApi();
  productlaneWindow.productlaneSkipLogged = false;
  productlaneWindow.productlaneCredentials = { widgetKey, userToken };

  if (productlaneWindow.productlaneInitialised) {
    productlane.set({ userToken, position: 'left' });
    loadProductlaneScript();
    return;
  }

  productlaneWindow.productlaneInitialised = true;
  productlane.on(PRODUCTLANE_LOADED_EVENT, () => {
    getProductlaneWindow().productlaneWidgetReady = true;
  });
  productlane.init({
    widgetKey,
    userToken,
    position: 'left',
  });
  loadProductlaneScript();
};

/**
 * Remembers the credentials without loading the widget. Accounts in their first
 * day must not get the changelog popup while they are still exploring the
 * product, but they keep full access to help and support: the first request
 * from the navigation starts the widget on demand.
 */
export const deferProductlaneWidget = (
  widgetKey: string,
  userToken: string,
): void => {
  if (!canUseBrowserApis()) {
    return;
  }

  if (!isProductlaneEnabled()) {
    resetProductlaneState();
    return;
  }

  const productlaneWindow = getProductlaneWindow();

  // The user may have opened the widget from the navigation already; keep its
  // session in sync with the refreshed user data instead of leaving it stale.
  if (productlaneWindow.productlaneInitialised) {
    initialiseProductlaneWidget(widgetKey, userToken);
    return;
  }

  productlaneWindow.productlaneCredentials = { widgetKey, userToken };
};

const startProductlaneWidget = (): boolean => {
  const productlaneWindow = getProductlaneWindow();
  if (productlaneWindow.productlaneInitialised) {
    return true;
  }

  const credentials = productlaneWindow.productlaneCredentials;
  if (!credentials) {
    return false;
  }

  initialiseProductlaneWidget(credentials.widgetKey, credentials.userToken);
  return true;
};

const openProductlaneView = async (view: ProductlaneView): Promise<boolean> => {
  if (!canUseBrowserApis()) {
    return false;
  }

  if (!isProductlaneEnabled()) {
    return false;
  }

  if (!startProductlaneWidget()) {
    console.error(`${PRODUCTLANE_LOG_PREFIX} ${view} requested before widget credentials were available.`);
    return false;
  }

  await waitForProductlaneWidget();

  const productlane = getProductlaneApi();
  if (!isBootstrappedProductlaneApi(productlane)) {
    console.error(`${PRODUCTLANE_LOG_PREFIX} ${view} requested but the widget did not become available.`);
    return false;
  }

  productlane.open(view);
  return true;
};

/**
 * Opens a widget view, starting the widget on demand for accounts whose widget
 * is deferred, and falls back to the help page when it stays unavailable.
 */
export const openProductlaneViewOrHelpPage = async (
  view: ProductlaneView,
  fallbackUrl: string = helpUrl,
): Promise<void> => {
  try {
    if (await openProductlaneView(view)) {
      return;
    }
  } catch (error: unknown) {
    console.error(`${PRODUCTLANE_LOG_PREFIX} Opening ${view} failed.`, error);
  }

  window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
};

export const resetProductlane = (): void => {
  resetProductlaneState();
};

export const signOutProductlane = (): void => {
  if (!canUseBrowserApis()) {
    return;
  }

  if (!isProductlaneEnabled()) {
    resetProductlaneState();
    return;
  }

  getProductlaneApi()?.signOut();
  resetProductlaneState();
};
